import React, { useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { spaEntityAPI } from '../../services/api';
import {
  Bars3Icon,
  BellIcon,
  ChevronDownIcon,
  MoonIcon,
  SunIcon,
  LanguageIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import useOrganizationSettings from '../../hooks/useOrganizationSettings';
import { APP_LOGO, APP_LOGO_ALT, APP_SHELL_GRADIENT_H, APP_SHELL_BORDER } from '../../constants/branding';
import { useTheme } from '../../contexts/ThemeContext';

const Header = ({ toggleMobileSidebar, toggleDesktopSidebar }) => {
  const { language, changeLanguage, t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const { data: notifications = [] } = useQuery(
    ['notifications'],
    () => spaEntityAPI.list('notifications'),
    { staleTime: 30_000 }
  );

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
    setNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    setUserMenuOpen(false);
  };

  const currentUser = user || {
    name: t('header.fallbackUserName'),
    role: t('header.fallbackUserRole'),
    avatar: null,
  };

  const { data: orgSettings } = useOrganizationSettings();
  const orgName = orgSettings?.gymName || 'FormaPro';

  const panelMotion = {
    initial: { opacity: 0, y: -6 },
    animate: { opacity: 1, y: 0 },
  };

  const menuButton =
    'rounded-lg p-2 text-white/90 transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40';

  return (
    <header className={`sticky top-0 z-30 border-b ${APP_SHELL_BORDER} ${APP_SHELL_GRADIENT_H}`}>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between sm:h-16">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              className={`${menuButton} lg:hidden`}
              onClick={toggleMobileSidebar}
            >
              <span className="sr-only">{t('header.openMenuSr')}</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            <button
              type="button"
              className={`${menuButton} hidden lg:inline-flex`}
              onClick={toggleDesktopSidebar}
            >
              <span className="sr-only">{t('header.toggleMenuSr')}</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            <Link to="/dashboard" className="ms-1 hidden min-w-0 items-center gap-2.5 sm:flex">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-soft ring-1 ring-red-100 dark:ring-red-900/40">
                <img src={APP_LOGO} alt={APP_LOGO_ALT} className="h-full w-full object-contain p-0.5" />
              </div>
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-sm font-semibold tracking-tight text-white">
                  {orgName}
                </span>
                <span className="truncate text-xs font-medium text-white/75">{t('header.brandLine')}</span>
              </div>
            </Link>

            <div className="ms-2 hidden min-w-0 flex-1 md:ms-8 md:block md:max-w-md lg:max-w-lg">
              <label className="relative block">
                <span className="sr-only">{t('header.searchSrOnly')}</span>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-white/50 ltr:left-3 rtl:right-3"
                  aria-hidden
                />
                <input
                  type="search"
                  placeholder={t('header.searchPlaceholder')}
                  className="w-full rounded-xl border border-white/20 bg-white/10 py-2 text-sm text-white placeholder:text-white/50 backdrop-blur-sm focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 ltr:pl-10 ltr:pr-3 rtl:pr-10 rtl:pl-3"
                />
              </label>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <button
              type="button"
              className={menuButton}
              onClick={() => toggleTheme()}
              aria-label={isDark ? t('header.themeLightAria') : t('header.themeDarkAria')}
              aria-pressed={isDark}
            >
              {isDark ? (
                <SunIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <MoonIcon className="h-5 w-5" aria-hidden="true" />
              )}
            </button>

            <button
              type="button"
              className={menuButton}
              onClick={() => changeLanguage(language === 'fr' ? 'ar' : 'fr')}
              aria-label={t('header.changeLangAria')}
            >
              <LanguageIcon className="h-5 w-5" aria-hidden="true" />
            </button>

            <div className="relative">
              <button type="button" className={menuButton} onClick={toggleNotifications}>
                <span className="sr-only">{t('header.notificationsSr')}</span>
                <BellIcon className="h-5 w-5" aria-hidden="true" />
                {notifications.length > 0 && (
                  <span className="absolute top-1 h-2 w-2 rounded-full bg-white ring-2 ring-forma-800 ltr:right-1 rtl:left-1" />
                )}
              </button>

              {notificationsOpen && (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-10 cursor-default bg-slate-950/20 dark:bg-black/40"
                    aria-label={t('header.notificationsCloseBackdrop')}
                    onClick={() => setNotificationsOpen(false)}
                  />
                  <motion.div
                    {...panelMotion}
                    className={clsx(
                      'absolute z-20 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-medium dark:border-slate-700 dark:bg-slate-900',
                      'ltr:right-0 ltr:origin-top-right rtl:left-0 rtl:origin-top-left'
                    )}
                  >
                    <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                        {t('header.notificationsTitle')}
                      </h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto main-scroll">
                      {notifications.length === 0 ? (
                        <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                          {t('header.notificationsEmpty')}
                        </p>
                      ) : (
                        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                          {notifications.map((notification) => (
                            <li
                              key={notification.id}
                              className={`px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/80 ${
                                !notification.read ? 'bg-forma-50/60 dark:bg-forma-950/20' : ''
                              }`}
                            >
                              <div className="flex justify-between gap-2">
                                <span className="font-medium text-slate-900 dark:text-white">
                                  {notification.title}
                                </span>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                  {notification.time}
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                                {notification.message}
                              </p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </motion.div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                type="button"
                className="ms-1 flex items-center gap-2 rounded-xl border border-transparent p-1.5 pe-2 text-left transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                onClick={toggleUserMenu}
              >
                <span className="sr-only">{t('header.userMenuSr')}</span>
                {currentUser.avatar ? (
                  <img className="h-8 w-8 rounded-full ring-2 ring-slate-200 dark:ring-slate-700" src={currentUser.avatar} alt="" />
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-forma-500 to-forma-700 text-xs font-bold text-white shadow-sm">
                    {currentUser.name.charAt(0)}
                  </div>
                )}
                <span className="hidden max-w-[8rem] truncate text-sm font-medium text-white sm:block">
                  {currentUser.name}
                </span>
                <ChevronDownIcon className="hidden h-4 w-4 text-white/70 sm:block" aria-hidden="true" />
              </button>

              {userMenuOpen && (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-10 cursor-default bg-slate-950/20 dark:bg-black/40"
                    aria-label={t('header.closeUserMenu')}
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <motion.div
                    {...panelMotion}
                    className={clsx(
                      'absolute z-20 mt-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-medium dark:border-slate-700 dark:bg-slate-900',
                      'ltr:right-0 ltr:origin-top-right rtl:left-0 rtl:origin-top-left'
                    )}
                  >
                    <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                        {currentUser.name}
                      </p>
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">{currentUser.role}</p>
                    </div>
                    <Link
                      to="/settings"
                      className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      {t('nav.settings')}
                    </Link>
                    <Link
                      to="/login"
                      className="block px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-950/30"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      {t('layout.logout')}
                    </Link>
                  </motion.div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
